<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePayrollStore } from '@/store/payroll-data'
import {
  DTextField,
  Loading02Icon,
  CheckCircleIcon,
  XCircleIcon,
  AlertTriangleIcon
} from 'front-library'
import { checkPayrollCodeAvailability } from '@/app/core/services/payrolls'

const { t } = useI18n()
const payrollStore = usePayrollStore()

const props = defineProps<{
  modelValue: string
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
}>()

// Guardar los valores originales al montar el componente
const originalCompanyCode = ref<string | undefined>()
const originalPayrollCode = ref<string | undefined>()

// Se inicializan una sola vez al montar
onMounted(() => {
  originalCompanyCode.value = payrollStore.payroll?.company?.code
  originalPayrollCode.value = props.modelValue
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'block-save': [blocked: boolean]
}>()

// Estados de validación
const isValidating = ref(false)
const validationResult = ref<any[] | null>(null)

let debounceTimeout: ReturnType<typeof setTimeout> | null = null

// Helper para saber si el valor es el original
const isEditingSame = computed(() => {
  const currentCompanyCode = payrollStore.payroll?.company?.code
  return (
    originalCompanyCode.value === currentCompanyCode &&
    originalPayrollCode.value === props.modelValue
  )
})

const validateCode = async () => {
  if (!props.modelValue || props.modelValue.trim() === '') {
    validationResult.value = null
    emit('block-save', false)
    return
  }

  isValidating.value = true
  emit('block-save', true)

  try {
    const result = await checkPayrollCodeAvailability(props.modelValue)
    validationResult.value = result

    if (Array.isArray(result) && result.length > 0) {
      const currentCompanyCode = payrollStore.payroll?.company?.code
      const exactMatch = result.find(
        item =>
          item.companyCode === currentCompanyCode &&
          item.payrollCode === props.modelValue
      )

      if (exactMatch && !isEditingSame.value) {
        emit('block-save', true)
      } else {
        emit('block-save', false)
      }
    } else {
      emit('block-save', false)
    }
  } catch (error) {
    console.error('Error validating payroll code:', error)
    validationResult.value = null
    emit('block-save', true)
  } finally {
    isValidating.value = false
  }
}

// Watch con debounce para validar el código
watch(
  () => props.modelValue,
  newValue => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout)
      debounceTimeout = null
    }
    if (newValue && newValue.trim() !== '') {
      debounceTimeout = setTimeout(() => {
        validateCode()
      }, 400)
    } else {
      validationResult.value = null
      emit('block-save', false)
    }
  },
  { immediate: true }
)

const validationState = computed(() => {
  if (isValidating.value) return 'validating'
  if (!validationResult.value) return 'none'
  if (
    Array.isArray(validationResult.value) &&
    validationResult.value.length === 0
  )
    return 'available'
  if (
    Array.isArray(validationResult.value) &&
    validationResult.value.length > 0
  ) {
    const currentCompanyCode = payrollStore.payroll?.company?.code
    const exactMatch = validationResult.value.find(
      item =>
        item.companyCode === currentCompanyCode &&
        item.payrollCode === props.modelValue
    )
    if (exactMatch && !isEditingSame.value) return 'error'
    if (
      validationResult.value.filter(
        item =>
          item.companyCode !== currentCompanyCode &&
          item.payrollCode === props.modelValue
      ).length > 0 &&
      !isEditingSame.value
    )
      return 'warning'
    return 'available'
  }
  return 'none'
})

const validationMessage = computed(() => {
  if (isValidating.value) {
    return t('payrolls.validation.checking')
  }

  if (!validationResult.value) return ''

  if (
    Array.isArray(validationResult.value) &&
    validationResult.value.length === 0
  ) {
    return t('payrolls.validation.available')
  }

  if (
    Array.isArray(validationResult.value) &&
    validationResult.value.length > 0
  ) {
    const currentCompanyCode = payrollStore.payroll?.company?.code

    const exactMatch = validationResult.value.find(
      item =>
        item.companyCode === currentCompanyCode &&
        item.payrollCode === props.modelValue
    )
    const otherCompanyMatches = validationResult.value.filter(
      item =>
        item.companyCode !== currentCompanyCode &&
        item.payrollCode === props.modelValue
    )

    if (exactMatch && !isEditingSame.value) {
      return [
        t('payrolls.validation.inUse', {
          payroll: exactMatch.payrollCode
        })
      ]
    } else if (otherCompanyMatches.length > 0 && !isEditingSame.value) {
      return otherCompanyMatches.map(item =>
        t('payrolls.validation.inUseOtherCompanies', {
          company: item.companyCode,
          payroll: item.payrollCode
        })
      )
    }
  }

  return ''
})

const stateClasses = computed(() => {
  switch (validationState.value) {
    case 'validating':
      return 'text-blue-600'
    case 'available':
      return 'text-green-600'
    case 'error':
      return 'text-red-600'
    case 'warning':
      return 'text-yellow-500'
    default:
      return ''
  }
})

const handleInput = (value: string) => {
  emit('update:modelValue', value)
}
</script>

<template>
  <div>
    <DTextField
      :model-value="modelValue"
      :label="label"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :error="error"
      type="string"
      @update:model-value="handleInput" />

    <div v-if="validationState !== 'none' && modelValue" class="mt-1">
      <div
        v-if="validationState === 'validating'"
        :class="stateClasses"
        class="flex items-center text-sm">
        <Loading02Icon class="w-4 h-4 mr-1 animate-spin" />
        <span style="white-space: pre-line">{{ validationMessage }}</span>
      </div>

      <div
        v-else-if="validationState === 'available'"
        :class="stateClasses"
        class="flex items-center text-sm font-medium">
        <CheckCircleIcon class="w-4 h-4 mr-1" />
        <span>{{ validationMessage }}</span>
      </div>

      <div
        v-else-if="validationState === 'error'"
        :class="stateClasses"
        class="flex flex-col items-start text-sm font-medium">
        <div
          v-for="(msg, idx) in validationMessage"
          :key="idx"
          class="flex items-start">
          <XCircleIcon class="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
          <span>{{ msg }}</span>
        </div>
      </div>
      <div
        v-else-if="validationState === 'warning'"
        :class="stateClasses"
        class="flex flex-col items-start text-sm font-medium">
        <div
          v-for="(msg, idx) in validationMessage"
          :key="idx"
          class="flex items-start">
          <AlertTriangleIcon class="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
          <span>{{ msg }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
